import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select, Tabs, Typography, message } from 'antd';
import { State, States } from 'sigt';
import { useWindowDimensions } from '../../../utils/hooks';
import { createFiscalCredit } from '../../../services/supportSuite';
import ModifyPayments from './ModifyPayments';
import ManageAlicuota from './ManageAlicuota';
import AdjustSettlementAmount from './AdjustSettlementAmount';
import ManageSettlements from './ManageSettlements';
import ResetPassword from './ResetPassword';

const { TabPane } = Tabs;

const SupportSuite: React.FC<SupportSuiteProps> = ({ thm }) => {
  const [form] = Form.useForm();
  const [tab, setTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('V');
  const [tipoCredito, setTipoCredito] = useState<'NATURAL' | 'JURIDICO'>('NATURAL');
  const { width } = useWindowDimensions();

  const tipoDocumentoAddon = useMemo(
    () => (
      <Select style={{ width: 72 }} value={tipoDocumento} onChange={(value) => setTipoDocumento(value)}>
          <Select.Option value='V'>V</Select.Option>
          <Select.Option value='E'>E</Select.Option>
          <Select.Option value='J'>J</Select.Option>
          <Select.Option value='G'>G</Select.Option>
          <Select.Option value='P'>P</Select.Option>
      </Select>
    ),
    [tipoDocumento]
  );

  const onFinish = async () => {
    const values = await form.validateFields();

    setLoading(true);
    const complete = message.loading('Cargando credito fiscal...', 0);
    try {
      await createFiscalCredit({
        tipo_documento: tipoDocumento,
        numero_documento: values.numero_documento,
        tipo_credito: values.tipo_credito,
        rim: values.rim,
        monto_credito: Number(values.monto_credito),
      });

      message.success('Credito cargado exitosamente');
      form.resetFields();
      setTipoDocumento('V');
      setTipoCredito('NATURAL');
      form.setFieldsValue({ tipo_credito: 'NATURAL' });
    } catch (error) {
      const apiError = error as any;
      message.error(apiError?.response?.data?.message || 'No fue posible cargar el credito fiscal');
    } finally {
      complete();
      setLoading(false);
    }
  };

  return (
    <Card
      style={{ height: '100%' }}
      title='Suite de Soporte'
      bodyStyle={{ height: 'calc(100% - 88px)', overflowY: 'auto', overflowX: 'hidden', border: '1px solid #f0f0f0' }}
      headStyle={{
        height: 64,
        backgroundColor: thm.primaryColor,
        padding: width < 992 ? '0 10px' : '0 20px',
        color: 'white',
      }}
    >
      <Tabs activeKey={tab} onChange={(key) => setTab(key)}>
        <TabPane style={{ padding: 8 }} tab='Crear Creditos Fiscales' key='1'>
          {/* ...Formulario de crear crédito fiscal... */}
          <Typography.Paragraph>
            Carga manual de credito fiscal para contribuyentes naturales o juridicos.
          </Typography.Paragraph>
          <Form
            layout='vertical'
            form={form}
            initialValues={{ tipo_documento: 'V', tipo_credito: 'NATURAL' }}
            onFinish={onFinish}
            scrollToFirstError
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name='numero_documento'
                  label='Documento de Identidad'
                  rules={[
                    { required: true, message: 'Debe indicar el documento' },
                    { pattern: /^\d+$/, message: 'El documento solo debe contener numeros' },
                  ]}
                >
                  <Input addonBefore={tipoDocumentoAddon} placeholder='Numero de documento' />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='tipo_credito'
                  label='Tipo de Credito'
                  rules={[{ required: true, message: 'Debe seleccionar el tipo' }]}
                >
                  <Select
                    placeholder='Seleccione'
                    onChange={(value: 'NATURAL' | 'JURIDICO') => {
                      setTipoCredito(value);
                      if (value === 'NATURAL') form.setFieldsValue({ tipo_credito: 'NATURAL', rim: undefined });
                    }}
                  >
                    <Select.Option value='NATURAL'>Natural</Select.Option>
                    <Select.Option value='JURIDICO'>Juridico</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              {tipoCredito === 'JURIDICO' && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name='rim'
                    label='R.I.M.'
                    rules={[
                      { required: true, message: 'Debe indicar el R.I.M. para creditos juridicos' },
                      { min: 3, message: 'El R.I.M. ingresado es invalido' },
                    ]}
                  >
                    <Input placeholder='Referencia municipal (R.I.M.)' />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} md={12}>
                <Form.Item
                  name='monto_credito'
                  label='Monto del Credito'
                  rules={[
                    { required: true, message: 'Debe indicar el monto del credito' },
                    { type: 'number', message: 'Debe ingresar un monto valido' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder='Monto'
                    precision={2}
                    parser={(value) => (value ? value.replace(/[^\d.-]/g, '') : '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Button loading={loading} type='primary' htmlType='submit'>
              Cargar Credito
            </Button>
          </Form>
        </TabPane>
        <TabPane style={{ padding: 8 }} tab='Modificar Pagos' key='2'>
          <ModifyPayments />
        </TabPane>
        <TabPane style={{ padding: 8 }} tab='Gestionar Alícuota Contribuyente' key='3'>
          <ManageAlicuota />
        </TabPane>
        <TabPane style={{ padding: 8 }} tab='Ajustar Monto Liquidacion' key='5'>
          <AdjustSettlementAmount />
        </TabPane>
        <TabPane style={{ padding: 8 }} tab='Gestionar Liquidaciones' key='6'>
          <ManageSettlements />
        </TabPane>
        <TabPane style={{ padding: 8 }} tab='Reseteo de Contraseña' key='7'>
          <ResetPassword />
        </TabPane>
      </Tabs>
    </Card>
  );
};

const mapStateToProps = (state: State) => ({ thm: state.thm, auth: state.auth });

export default connect(mapStateToProps)(SupportSuite);

interface SupportSuiteProps {
  thm: States.ThemeColors;
  auth: States.Auth;
}
